import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Currency, UsersSelf } from '@freelancer/datastore/collections';
import { Deposits } from '@freelancer/deposits';
import { PaymentsUtils } from '@freelancer/payments-utils';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { PictureDisplay, PictureImage } from '@freelancer/ui/picture';
import { SelectItem } from '@freelancer/ui/select';
import { FontWeight } from '@freelancer/ui/text';
import { isFormControl } from '@freelancer/utils';
import {
  DepositMethodApi,
  DepositRequestApi,
} from 'api-typings/payments/payments';
import * as Rx from 'rxjs';
import { SourceDisplayMode } from '../../../../common/source-template/source-template.component';
import { DepositData, GenericPayload } from '../../../../common/types';

@Component({
  selector: 'app-ideal-method',
  template: `
    <app-source-template
      [group]="group"
      [sourceRadioValue]="id"
      [sourceDisplayMode]="sourceDisplayMode"
    >
      <app-source-template-header>
        <app-source-template-header-title i18n="iDEAL - name of the provider">
          iDEAL
        </app-source-template-header-title>
        <app-source-template-logo
          [image]="idealLogo"
        ></app-source-template-logo>
      </app-source-template-header>
      <app-source-template-body>
        <fl-label
          i18n="Ideal deposit method - bank select label"
          [for]="'IdealBank'"
        >
          Select your bank:
        </fl-label>
        <ng-container *ngIf="bankSelector.get('bankSelect') as control">
          <fl-select
            *ngIf="isFormControl(control)"
            class="IdealBankDropdown"
            flTrackingLabel="idealBankDropdown"
            [control]="control"
            [id]="'IdealBank'"
            [options]="idealBankList"
          ></fl-select>
        </ng-container>
      </app-source-template-body>
    </app-source-template>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./ideal-method.component.scss'],
})
export class IdealMethodComponent implements OnInit {
  FontWeight = FontWeight;
  isFormControl = isFormControl;
  idealBankList: readonly SelectItem[] = [
    {
      displayText: 'Rabobank',
      value: '0021',
    },
    {
      displayText: 'ABN Amro',
      value: '0031',
    },
    {
      displayText: 'Van Lanschot Bankiers',
      value: '0161',
    },
    {
      displayText: 'Triodos Bank',
      value: '0511',
    },
    {
      displayText: 'ING',
      value: '0721',
    },
    {
      displayText: 'SNS Bank',
      value: '0751',
    },
    {
      displayText: 'ASN Bank',
      value: '0761',
    },
    {
      displayText: 'RegioBank',
      value: '0771',
    },
    {
      displayText: 'Knab',
      value: '0801',
    },
    {
      displayText: 'bunq',
      value: '0802',
    },
  ];

  IconColor = IconColor;
  IconSize = IconSize;
  PictureDisplay = PictureDisplay;
  SourceDisplayMode = SourceDisplayMode;

  idealLogo: PictureImage = {
    alt: 'ideal',
    src: this.paymentsUtils.getMethodLogoPath(DepositMethodApi.IDEAL),
  };
  bankSelector: FormGroup;

  @Input() backUrlEncoded?: string;
  @Input() genericPayload: GenericPayload;
  @Input() depositFormGroup: FormGroup;
  @Input() group: FormGroup;
  @Input() id: string;
  @Input() userInfo$: Rx.Observable<UsersSelf>;
  @Input() sourceDisplayMode: SourceDisplayMode;
  @Input() set depositData(depositData: DepositData) {
    if (depositData) {
      this.handleDepositDataChanges(depositData);
    }
  }

  @Output() paymentCurrencySelected = new EventEmitter<Currency>();

  constructor(
    private fb: FormBuilder,
    private deposits: Deposits,
    private paymentsUtils: PaymentsUtils,
  ) {}

  ngOnInit() {
    this.bankSelector = this.fb.group({
      bankSelect: [this.idealBankList[0].value, [Validators.required]],
    });
    this.paymentsUtils.registerFormGroup(
      this.bankSelector,
      this.depositFormGroup,
      this.id,
    );
  }

  handleDepositDataChanges(depositData: DepositData) {
    const payload: DepositRequestApi = {
      ...this.genericPayload,
      deposit_method: DepositMethodApi.IDEAL,
      amount: depositData.depositAmount,
      currency_id: depositData.paymentCurrency.id,
      ideal_bank_id: this.bankSelector.value.bankSelect,
      confirm_url: this.deposits.getConfirmUrl({
        backUrl: this.backUrlEncoded,
      }),
    };

    this.deposits.makeDeposit({ payload, enablePopup: false });
  }
}
